import {
  Alert,
  Button,
  Stack,
  Typography,
  Link,
  CircularProgress,
} from '@mui/material'
import React, { useState, useEffect, useRef } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { blue } from '@mui/material/colors'
import { TitledPaper } from '@dai/components'
import { ActionCodeInfo } from 'firebase/auth'

interface RecoverEmailProps {
  oobCode: string
  signInUrl: string
  checkActionCode: (code: string) => Promise<ActionCodeInfo>
  verifyAndRecoverEmail: (code: string) => Promise<void>
}

const RecoverEmail: React.FC<RecoverEmailProps> = ({
  oobCode,
  signInUrl,
  checkActionCode,
  verifyAndRecoverEmail,
}) => {
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<boolean>(false)
  const [email, setEmail] = useState<string | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const verificationAttempted = useRef<boolean>(false)

  useEffect(() => {
    const verifyCode = async () => {
      if (verificationAttempted.current) {
        return
      }

      verificationAttempted.current = true

      try {
        setLoading(true)
        const actionCodeInfo = await checkActionCode(oobCode)
        setEmail(actionCodeInfo.data.email || null)
      } catch (error: any) {
        setError(error.message || 'Invalid or expired action code')
      } finally {
        setLoading(false)
      }
    }

    verifyCode()
  }, [oobCode, checkActionCode])

  const handleRecover = async () => {
    try {
      setLoading(true)
      await verifyAndRecoverEmail(oobCode)
      setSuccess(true)
    } catch (error: any) {
      setError(error.message || 'Something went wrong')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <TitledPaper title="Recover Email" subtitle="Verifying your request...">
        <CircularProgress />
      </TitledPaper>
    )
  }

  return (
    <TitledPaper
      title="Recover Email"
      subtitle={
        success ? null : email ? (
          <>
            You're about to recover your email:{' '}
            <span style={{ color: blue[500] }}>{email}</span>
          </>
        ) : (
          'Recover your email address'
        )
      }
    >
      <Stack spacing={4}>
        {error ? (
          <Alert severity="warning">{error}</Alert>
        ) : success ? (
          <Alert severity="success">
            Your email has been restored successfully. You can now sign in with
            your original email.
          </Alert>
        ) : null}
        {email && !success && !error && (
          <>
            <Typography variant="body2">
              Your email was changed to another address. This recovery link
              allows you to restore your original email address.
            </Typography>
            <Button
              fullWidth
              color="primary"
              variant="contained"
              onClick={handleRecover}
              disabled={loading}
              startIcon={
                loading ? <CircularProgress size={20} color="inherit" /> : null
              }
            >
              Recover Email
            </Button>
          </>
        )}
        <Typography variant="body2" color="text.secondary" align="center">
          <Link component={RouterLink} to={signInUrl}>
            Return to sign in
          </Link>
        </Typography>
      </Stack>
    </TitledPaper>
  )
}

export default RecoverEmail
